import React, { useEffect, useState, useMemo } from "react";

import {
  Container,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownItem,
  DropdownMenu,
  Nav,
  NavItem,
  NavLink,
  Row,
  Card,
  CardHeader,
  CardBody,
  Col,
  Modal,
  ModalHeader,
  ModalBody,
  Button,
  Input,
  Label,
  FormFeedback,
  TabContent,
  TabPane,
  Form,
} from "reactstrap";
import classnames from "classnames";
import { useFormik } from "formik";
import * as Yup from "yup";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import Dropzone from "react-dropzone";

// RangeSlider
import Nouislider from "nouislider-react";
import "nouislider/distribute/nouislider.css";
import DeleteModal from "../../../Components/Common/DeleteModal";

import BreadCrumb from "../../../Components/Common/BreadCrumb";
import TableContainer from "../../../Components/Common/TableContainer";
import { Rating, Published, Price } from "./EcommerceProductCol";

//Import actions
import { 
  getProducts as onGetProducts, 
  deleteProducts,
  addNewProduct,
  updateProduct,
  getEcommerceCategories,
  getSubcategories,
  getCommissions,
  getTaxes
} from "../../../slices/thunks";
import { isEmpty } from "lodash";
import Select from "react-select";

//redux
import { useSelector, useDispatch } from "react-redux";
import { Link, useLocation } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import { getUserRole } from "../../../helpers/role_helper";
import { getLoggedinUser } from "../../../api";
import { resolveProductImageSrc, getProductImageField } from "../../../helpers/productImageUrl";
import ProductVariantManager from "./ProductVariantManager";

const EcommerceProducts = (props) => {
  const dispatch = useDispatch();
  const location = useLocation();

  // Get user info for filtering
  const userProfile = getLoggedinUser();
  const userRole = getUserRole(userProfile);
  const userId = userProfile?.id || userProfile?.uid || userProfile?._id;
  
  // Check if this is accessed from Seller Products menu
  // Show Store dropdown only for sellers or when accessed from seller products route
  const showStoreDropdown = userRole === "seller" || location.pathname.includes("/seller");
  
  // Determine if we're viewing seller products section (admin viewing seller products)
  const isSellerProductsSection = location.pathname === "/seller-products";

  // Get products from Redux state - ensure it's always an array
  const productsRaw = useSelector((state) => state.Ecommerce?.products);
  const products = Array.isArray(productsRaw) ? productsRaw : [];
  
  const categoriesRaw = useSelector((state) => state.Ecommerce?.categories);
  const categories = Array.isArray(categoriesRaw) ? categoriesRaw : [];
  
  const subcategoriesRaw = useSelector((state) => state.Ecommerce?.subcategories);
  const subcategories = Array.isArray(subcategoriesRaw) ? subcategoriesRaw : [];

  // Get commissions from Redux (only active ones)
  const commissionsRaw = useSelector((state) => state.Ecommerce?.commissions);
  const allCommissions = Array.isArray(commissionsRaw) ? commissionsRaw : [];
  const commissionType = allCommissions
    .filter(comm => comm.status === 'Active')
    .map(comm => {
      // Ensure value has % symbol for display
      const displayValue = comm.value.includes('%') ? comm.value : `${comm.value}%`;
      return { label: displayValue, value: comm.value };
    });

  // Get taxes from Redux (only active ones)
  const taxesRaw = useSelector((state) => state.Ecommerce?.taxes);
  const allTaxes = Array.isArray(taxesRaw) ? taxesRaw : [];
  const taxType = allTaxes
    .filter(tax => tax.status === 'Active')
    .map(tax => {
      // Ensure value has % symbol for display
      const displayValue = tax.value.includes('%') ? tax.value : `${tax.value}%`;
      return { label: displayValue, value: tax.value };
    });

  const [productList, setProductList] = useState([]);
  const [activeTab, setActiveTab] = useState("1");
  const [selectedMulti, setselectedMulti] = useState(null);
  const [product, setProduct] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());

  // Modal states
  const [modal, setModal] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [productData, setProductData] = useState(null);
  const [customActiveTab, setcustomActiveTab] = useState("1");
  const [selectedFiles, setselectedFiles] = useState([]);
  const [selectedVisibility, setselectedVisibility] = useState(null);
  const [productImage, setProductImage] = useState(null);
  const [existingImage, setExistingImage] = useState(null);

  // Variant & Option states
  const [hasVariants, setHasVariants] = useState(false);
  const [options, setOptions] = useState([]);
  const [variants, setVariants] = useState([]);


  const productVisibility = [
    { label: "Hidden", value: "Hidden" },
    { label: "Public", value: "Public" },
    { label: "Block", value: "Block" },
    { label: "Banned", value: "Banned" },
  ];

  function handleAcceptedFiles(files) {
    const filesWithPreview = files.map((file) =>
      Object.assign(file, {
        preview: URL.createObjectURL(file),
        formattedSize: formatBytes(file.size),
      })
    );
    // Append new files to existing ones
    setselectedFiles(prevFiles => [...prevFiles, ...filesWithPreview]);
  }

  function formatBytes(bytes, decimals = 2) {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + " " + sizes[i];
  }

  const toggleCustom = (tab) => {
    if (customActiveTab !== tab) {
      setcustomActiveTab(tab);
    }
  };

  // Delete modal states
  const [deleteModal, setDeleteModal] = useState(false);
  const [deleteModalMulti, setDeleteModalMulti] = useState(false);

  // Price range (for slider)
  const [priceRange, setPriceRange] = useState([0, 2000]);

  // Rating values for filtering (example placeholder)
  const [ratingvalues, setRatingvalues] = useState([]);

  // Fetch products, categories, and subcategories on mount
  useEffect(() => {
    // Build query params based on user role
    const params = {};
    if (userRole === "seller" && userId) {
      params.seller_id = userId;
    }
    
    // Fetch products with seller_id filter for sellers
    dispatch(onGetProducts(params));
    
    // Fetch categories, subcategories, commissions, and taxes
    dispatch(getEcommerceCategories(params));
    dispatch(getSubcategories(params));
    dispatch(getCommissions()); // Fetch commissions (admin only, but needed for product forms)
    dispatch(getTaxes()); // Fetch taxes (admin only, but needed for product forms)
  }, [dispatch, userRole, userId]); // Re-fetch if user role or ID changes

  // Filter subcategories based on selected category (for edit mode)
  const filteredSubcategoriesForEdit = useMemo(() => {
    if (!productData?.category_id) return [];
    return subcategories.filter(
      (subcat) => {
        const subcatCategoryId = String(subcat.category_id || subcat.categoryId || "");
        const selectedId = String(productData.category_id);
        return subcatCategoryId === selectedId;
      }
    );
  }, [subcategories, productData?.category_id]);

  // Validation schema
  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: productData?.name || "",
      category_id: productData?.category_id || productData?.categoryId || "",
      subcategory_id: productData?.subcategory_id || productData?.subcategoryId || "",
      price: productData?.price || "",
      purchase_price: productData?.cost_per_item || productData?.purchase_price || "",
      stock: productData?.stock || productData?.sku || "", // SKU: backend returns sku, form uses stock
      stock_quantity: productData?.stock_quantity ?? productData?.stock ?? 0, // QTY: actual inventory count
      product_discount: productData?.product_discount || "",
      commission: productData?.commission || "",
      tax: productData?.tax || "",
      description: productData?.description || "",
      short_description: productData?.short_description || "",
      meta_title: productData?.meta_title || "",
      meta_keyword: productData?.meta_keyword || "",
      meta_description: productData?.meta_description || "",
      product_tags: productData?.product_tags || "",
      status: productData?.status || productData?.type || "draft",
      visibility: productData?.visibility || "Public",
      // Product attributes/features
      material: productData?.material || "",
      weight: productData?.weight || "",
      dimensions_length: productData?.dimensions?.length || "",
      dimensions_width: productData?.dimensions?.width || "",
      dimensions_height: productData?.dimensions?.height || "",
      features: productData?.features || "", // Comma-separated or newline-separated features
    },
    validationSchema: Yup.object({
      name: Yup.string().required("Product name is required"),
      category_id: Yup.string().required("Category is required"),
      subcategory_id: Yup.string().required("Subcategory is required"),
      price: Yup.number()
        .nullable()
        .transform((v, o) => (o === "" || o === null || o === undefined ? null : v))
        .test("price-check", "Price is required and must be positive", function (val) {
          if (hasVariants) return true;
          return val != null && val >= 0;
        }),
      purchase_price: Yup.number().nullable().min(0, "Purchase Price must be positive").transform((v, o) => (o === "" || o === null || o === undefined ? null : v)),
      stock: Yup.string().test("sku-check", "SKU is required", function (val) {
        if (hasVariants) return true;
        return !!val && val.trim().length > 0;
      }),
      stock_quantity: Yup.number().min(0, "Stock QTY must be 0 or more").nullable().transform((v, o) => (o === "" || o === null || o === undefined ? 0 : v)),
      product_discount: Yup.string(),
      commission: Yup.string(),
      tax: Yup.string(),
      description: Yup.string(),
      short_description: Yup.string(),
      meta_title: Yup.string(),
      meta_keyword: Yup.string(),
      meta_description: Yup.string(),
      product_tags: Yup.string(),
      status: Yup.string().oneOf(["published", "draft", "scheduled"], "Invalid status"),
      visibility: Yup.string(),
      material: Yup.string(),
      weight: Yup.number().nullable().min(0, "Weight must be positive").transform((v, o) => (o === "" || o === null || o === undefined ? null : v)),
      dimensions_length: Yup.number().nullable().min(0, "Length must be positive").transform((v, o) => (o === "" || o === null || o === undefined ? null : v)),
      dimensions_width: Yup.number().nullable().min(0, "Width must be positive").transform((v, o) => (o === "" || o === null || o === undefined ? null : v)),
      dimensions_height: Yup.number().nullable().min(0, "Height must be positive").transform((v, o) => (o === "" || o === null || o === undefined ? null : v)),
      features: Yup.string(),
    }),
    onSubmit: async (values) => {
      // Validate variants if variable product is enabled
      if (hasVariants) {
        if (!variants || variants.length === 0) {
          toast.error("Please generate or add at least one variant before submitting.");
          return;
        }
        const activeVariants = variants.filter((v) => v.status !== "disabled");
        if (activeVariants.length === 0) {
          toast.error("Please enable at least one active variant.");
          return;
        }
        const invalidPrice = activeVariants.find(
          (v) => v.price === "" || v.price === null || isNaN(parseFloat(v.price)) || parseFloat(v.price) < 0
        );
        if (invalidPrice) {
          toast.error("Please provide a valid price for all active variants.");
          return;
        }
      }

      // Create FormData to handle file upload
      const formData = new FormData();

      // Calculate dynamic price and stock from variants if enabled
      let finalPrice = parseFloat(values.price) || 0;
      let finalStockQty = parseInt(values.stock_quantity, 10) || 0;
      let finalSku = values.stock;

      if (hasVariants && variants.length > 0) {
        const activeVars = variants.filter((v) => v.status !== "disabled");
        const prices = (activeVars.length > 0 ? activeVars : variants)
          .map((v) => parseFloat(v.price))
          .filter((p) => !isNaN(p) && p > 0);
        if (prices.length > 0) {
          finalPrice = Math.min(...prices);
        }
        finalStockQty = variants.reduce(
          (sum, v) => sum + (parseInt(v.stock_quantity, 10) || 0),
          0
        );
        if (!finalSku && variants[0]?.sku) {
          finalSku = variants[0].sku;
        }
      }
      
      // Append all product data
      formData.append('name', values.name);
      formData.append('category_id', values.category_id);
      formData.append('subcategory_id', values.subcategory_id);
      formData.append('price', String(finalPrice));
      if (values.purchase_price) {
        formData.append('purchase_price', parseFloat(values.purchase_price));
      } else {
        formData.append('purchase_price', '');
      }
      formData.append('stock', finalSku || '');
      formData.append('stock_quantity', String(finalStockQty));
      formData.append('product_discount', values.product_discount || '');
      formData.append('commission', values.commission || '');
      formData.append('tax', values.tax || '');
      formData.append('description', values.description || '');
      formData.append('short_description', values.short_description || '');
      formData.append('meta_title', values.meta_title || '');
      formData.append('meta_keyword', values.meta_keyword || '');
      formData.append('meta_description', values.meta_description || '');
      formData.append('product_tags', values.product_tags || '');
      formData.append('status', values.status);
      formData.append('visibility', values.visibility);
      // Product attributes/features
      formData.append('material', values.material || '');
      formData.append('weight', values.weight || '');
      // Build dimensions object
      const dimensions = {};
      if (values.dimensions_length) dimensions.length = values.dimensions_length;
      if (values.dimensions_width) dimensions.width = values.dimensions_width;
      if (values.dimensions_height) dimensions.height = values.dimensions_height;
      formData.append('dimensions', JSON.stringify(Object.keys(dimensions).length > 0 ? dimensions : null));
      formData.append('features', values.features || '');

      // Variant & Option attributes
      formData.append('has_variants', hasVariants ? '1' : '0');
      formData.append('options', JSON.stringify(hasVariants ? options : []));
      formData.append('variants', JSON.stringify(hasVariants ? variants : []));

      // seller_id: NULL for admin products, user ID for seller products
      if (userRole === "seller" && userId) {
        formData.append('seller_id', userId);
      } else {
        // Admin creates products with seller_id = null (admin products)
        formData.append('seller_id', '');
      }
      // Always set created_by to current user ID (admin or seller)
      if (userId) {
        formData.append('created_by', userId);
      }
      
      // Append main product image (thumbnail) if selected
      if (productImage) {
        // New image file selected - will be uploaded
        formData.append('productImage', productImage);
      } else if (isEdit) {
        // If editing and no new image selected, send existing image filename or empty string if removed
        formData.append('image', existingImage || '');
      }
      
      // Append gallery images (multiple images) if any selected
      let hasNewGalleryImages = false;
      if (selectedFiles && selectedFiles.length > 0) {
        selectedFiles.forEach((file, index) => {
          // Only append actual File objects (not preview objects from existing images)
          if (file instanceof File) {
            formData.append('galleryImages', file);
            hasNewGalleryImages = true;
          }
        });
      }
      
      // If editing, preserve only the retained existing gallery images (not removed)
      if (isEdit) {
        const retainedExistingImages = (selectedFiles || [])
          .filter(f => !(f instanceof File) && f.name)
          .map(f => f.name);
        formData.append('existingGalleryImages', JSON.stringify(retainedExistingImages));
      }

      // Create payload object for Redux (FormData will be handled by API)
      // For edit mode, we need to include the ID in the formData as well
      if (isEdit) {
        formData.append('_id', productData._id || productData.id);
      }
      
      const productPayload = {
        formData: formData,
        _id: isEdit ? (productData._id || productData.id) : undefined,
      };

      if (isEdit) {
        dispatch(updateProduct(productPayload));
      } else {
        dispatch(addNewProduct(productPayload));
      }
      toggle();
      validation.resetForm();
      setselectedFiles([]);
      setProductImage(null);
    },
  });

  // Filter subcategories based on form category selection (for add/edit form)
  // This must be defined AFTER validation to avoid "Cannot access before initialization" error
  const filteredSubcategories = useMemo(() => {
    const selectedCategoryId = validation.values.category_id;
    if (!selectedCategoryId) return [];
    
    return subcategories.filter((subcat) => {
      // Convert both to strings for comparison to handle type mismatches
      const subcatCategoryId = String(subcat.category_id || subcat.categoryId || "");
      const selectedId = String(selectedCategoryId);
      return subcatCategoryId === selectedId;
    });
  }, [subcategories, validation.values.category_id]);

  // Toggle modal
  const toggle = () => {
    if (modal) {
      setModal(false);
      setProductData(null);
      setIsEdit(false);
      setcustomActiveTab("1");
      setselectedFiles([]);
      setProductImage(null);
      setExistingImage(null);
      setselectedVisibility(null);
      setHasVariants(false);
      setOptions([]);
      setVariants([]);
      validation.resetForm();
    } else {
      setModal(true);
    }
  };

  // Handle edit click
  const handleEditClick = (product) => {
    setProductData(product);
    setIsEdit(true);
    setModal(true);
    // Reset product image state and track existing main image
    setProductImage(null);
    setExistingImage(product.thumbnail || product.image || null);
    
    // Load existing gallery images if any
    if (product.images) {
      try {
        const existingImages = typeof product.images === 'string' 
          ? JSON.parse(product.images) 
          : product.images;
        // Convert existing image filenames to file preview objects for display
        if (Array.isArray(existingImages) && existingImages.length > 0) {
          const previewFiles = existingImages.map(filename => ({
            name: filename,
            preview: resolveProductImageSrc(filename) || "",
            formattedSize: 'Existing'
          }));
          setselectedFiles(previewFiles);
        }
      } catch (e) {
        console.error('Error parsing existing gallery images:', e);
        setselectedFiles([]);
      }
    } else {
      setselectedFiles([]);
    }

    // Parse attributes and variants
    let attributesObj = {};
    if (product.attributes) {
      try {
        attributesObj = typeof product.attributes === 'string' ? JSON.parse(product.attributes) : product.attributes;
      } catch (e) {
        attributesObj = {};
      }
    }

    const prodVariants = product.variants || attributesObj.variants || [];
    const prodOptions = product.options || attributesObj.options || [];
    const prodHasVariants = product.has_variants === 1 || product.has_variants === true || attributesObj.has_variants === true || (Array.isArray(prodVariants) && prodVariants.length > 0);

    setHasVariants(prodHasVariants);
    setOptions(Array.isArray(prodOptions) ? prodOptions : []);
    setVariants(Array.isArray(prodVariants) ? prodVariants : []);
    
    // Set form values
    validation.setFieldValue("name", product.name || "");
    validation.setFieldValue("category_id", product.category_id || product.categoryId || "");
    validation.setFieldValue("subcategory_id", product.subcategory_id || product.subcategoryId || "");
    validation.setFieldValue("price", product.price || "");
    validation.setFieldValue("purchase_price", product.cost_per_item || product.purchase_price || "");
    validation.setFieldValue("stock", product.stock || product.sku || ""); // SKU: backend has sku
    validation.setFieldValue("stock_quantity", product.stock_quantity ?? product.stock ?? 0);
    validation.setFieldValue("product_discount", product.product_discount || "");
    validation.setFieldValue("commission", product.commission || "");
    validation.setFieldValue("tax", product.tax || "");
    validation.setFieldValue("description", product.description || "");
    validation.setFieldValue("short_description", product.short_description || "");
    validation.setFieldValue("meta_title", product.meta_title || "");
    validation.setFieldValue("meta_keyword", product.meta_keyword || "");
    validation.setFieldValue("meta_description", product.meta_description || "");
    validation.setFieldValue("product_tags", product.product_tags || "");
    validation.setFieldValue("status", product.status || product.type || "draft");
    validation.setFieldValue("visibility", product.visibility || "Public");
    // Product attributes/features
    validation.setFieldValue("material", product.material || attributesObj.material || "");
    validation.setFieldValue("weight", product.weight || attributesObj.weight || "");
    // Parse dimensions
    const dimensions = typeof product.dimensions === 'string' ? JSON.parse(product.dimensions || '{}') : (product.dimensions || attributesObj.dimensions || {});
    validation.setFieldValue("dimensions_length", dimensions.length || "");
    validation.setFieldValue("dimensions_width", dimensions.width || "");
    validation.setFieldValue("dimensions_height", dimensions.height || "");
    // Parse features (if stored as array, join with newlines)
    const features = Array.isArray(product.features || attributesObj.features) 
      ? (product.features || attributesObj.features).join('\n') 
      : (typeof product.features === 'string' ? product.features : '');
    validation.setFieldValue("features", features);
    
    if (product.visibility) {
      setselectedVisibility({ label: product.visibility, value: product.visibility });
    }
  };

  // Track previous category_id to detect changes
  const prevCategoryIdRef = React.useRef(validation.values.category_id);
  
  // Update subcategory options when category changes
  useEffect(() => {
    const currentCategoryId = validation.values.category_id;
    const prevCategoryId = prevCategoryIdRef.current;
    
    // Only reset subcategory if category actually changed (not on initial render)
    if (currentCategoryId && prevCategoryId && currentCategoryId !== prevCategoryId) {
      validation.setFieldValue("subcategory_id", "");
    }
    
    // Update ref for next render
    prevCategoryIdRef.current = currentCategoryId;
  }, [validation.values.category_id]);

  // Whenever products or priceRange or activeTab filter changes, compute productList
  useEffect(() => {
    let filtered = Array.isArray(products) ? [...products] : [];

    // Filter by seller_id for sellers (additional safety check)
    // Sellers should only see their own products (seller_id matches their ID)
    // Admin products have seller_id = NULL, so they should be excluded
    if (userRole === "seller" && userId) {
      filtered = filtered.filter((p) => {
        // Exclude products where seller_id is NULL (admin products)
        if (p.seller_id === null || p.seller_id === undefined) {
          return false;
        }
        // Only include products where seller_id matches the current seller's ID
        const productSellerId = p.seller_id?.toString();
        const currentUserId = userId?.toString();
        return productSellerId === currentUserId;
      });
    } else if (userRole === "admin") {
      // Admin Products section (/products) - show ALL products so admin can view every uploaded product
      // Seller Products section (/seller-products) - show only seller products (seller_id IS NOT NULL)
      if (isSellerProductsSection) {
        filtered = filtered.filter((p) => p.seller_id !== null && p.seller_id !== undefined);
      }
      // On main /products page: no filter by seller_id — show all products
    }

    // Tab filter
    if (activeTab === "2") {
      filtered = filtered.filter((p) => p.status === "published" || p.type === "published");
    } else if (activeTab === "3") {
      filtered = filtered.filter((p) => p.status === "draft" || p.type === "draft" || (!p.status && !p.type));
    }

    // Price filter
    if (priceRange && priceRange.length === 2) {
      const [minP, maxP] = priceRange;
      filtered = filtered.filter((p) => {
        const price = typeof p.price === "number" ? p.price : Number(p.price || 0);
        return price >= minP && price <= maxP;
      });
    }

    // Rating filter (if implemented)
    if (ratingvalues && ratingvalues.length > 0) {
      const minRating = Math.min(...ratingvalues);
      filtered = filtered.filter((p) => (p.rating || 0) >= minRating);
    }

    setProductList(filtered);
  }, [products, activeTab, priceRange, ratingvalues, userRole, userId, isSellerProductsSection]);

  // map productList for table: ensure 'id' exists and image path
  const tableData = useMemo(() => {
    return (productList || []).map((p) => {
      // Find category and subcategory names
      const category = categories.find(
        (cat) => cat.id === p.category_id || cat._id === p.category_id || cat.id === p.categoryId || cat._id === p.categoryId
      );
      const subcategory = subcategories.find(
        (subcat) => subcat.id === p.subcategory_id || subcat._id === p.subcategory_id || subcat.id === p.subcategoryId || subcat._id === p.subcategoryId
      );

      // Get seller name from product data (joined from backend)
      let sellerName = "Admin";
      if (p.seller_id && (p.company_name || p.first_name || p.last_name)) {
        sellerName = p.company_name || `${p.first_name || ''} ${p.last_name || ''}`.trim() || p.seller_email || "Seller";
      }

      return {
        ...p,
        id: p._id || p.id || Math.random().toString(36).slice(2, 9),
        categoryName: category?.name || p.category || "N/A",
        subcategoryName: subcategory?.name || "N/A",
        sellerName: sellerName,
      };
    });
  }, [productList, categories, subcategories]);

  const toggleTab = (tab, type) => {
    if (activeTab !== tab) {
      setActiveTab(tab);
    }
  };

  const onClickDelete = (product) => {
    setProduct(product);
    setDeleteModal(true);
  };

  const handleDeleteProduct = () => {
    if (product && (product._id || product.id)) {
      dispatch(deleteProducts(product._id || product.id));
      setDeleteModal(false);
      // remove from local selection if present
      setSelectedIds((prev) => {
        const copy = new Set(prev);
        copy.delete(product._id || product.id);
        return copy;
      });
    }
  };

  const handleCheckboxChange = (id, checked) => {
    setSelectedIds((prev) => {
      const copy = new Set(prev);
      if (checked) copy.add(id);
      else copy.delete(id);
      return copy;
    });
  };

  const deleteMultiple = () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    ids.forEach((id) => {
      dispatch(deleteProducts(id));
    });
    // give a tiny delay for UX (to allow toasts) then clear local selection
    setTimeout(() => {
      toast.clearWaitingQueue();
      setSelectedIds(new Set());
      setDeleteModalMulti(false);
    }, 500);
  };

  // Price slider update
  const onPriceUpdate = (values) => {
    const numeric = values.map((v) => Number(v));
    setPriceRange(numeric);
  };

  const columns = useMemo(
    () => [
      {
        header: "#",
        accessorKey: "id",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cell) => {
          const id = cell.row.original._id || cell.getValue();
          const checked = selectedIds.has(id);
          return (
            <input
              type="checkbox"
              className="productCheckBox form-check-input"
              value={id}
              checked={checked}
              onChange={(e) => handleCheckboxChange(id, e.target.checked)}
            />
          );
        },
      },
      {
        header: "Product",
        accessorKey: "name",
        enableColumnFilter: false,
        cell: (cell) => (
          <>
            <div className="d-flex align-items-center">
              <div className="flex-shrink-0 me-3">
                <div className="avatar-sm bg-light rounded p-1">
                  <img
                    src={(() => {
                      const imageField = getProductImageField(cell.row.original);
                      const url = resolveProductImageSrc(imageField);
                      return url || "https://via.placeholder.com/100?text=No+Image";
                    })()}
                    alt={cell.row.original.name || "Product"}
                    className="img-fluid d-block"
                    style={{ maxWidth: "100%", height: "auto", objectFit: "cover" }}
                    onError={(e) => {
                      if (e.target.src !== "https://via.placeholder.com/100?text=No+Image") {
                        e.target.src = "https://via.placeholder.com/100?text=No+Image";
                      }
                    }}
                  />
                </div>
              </div>
              <div className="flex-grow-1">
                <h5 className="fs-14 mb-1">
                  <Link to="/apps-ecommerce-product-details" className="text-body">
                    {cell.getValue()}
                  </Link>
                </h5>
                <p className="text-muted mb-0">
                  Category : <span className="fw-medium"> {cell.row.original.categoryName}</span>
                  {cell.row.original.subcategoryName && cell.row.original.subcategoryName !== "N/A" && (
                    <> | Subcategory : <span className="fw-medium"> {cell.row.original.subcategoryName}</span></>
                  )}
                </p>
              </div>
            </div>
          </>
        ),
      },
      // Show seller name column only in seller products section
      ...(isSellerProductsSection ? [{
        header: "Seller",
        accessorKey: "sellerName",
        enableColumnFilter: false,
        cell: (cell) => (
          <span className="fw-medium">{cell.getValue() || "N/A"}</span>
        ),
      }] : []),
      {
        header: "Stock",
        accessorKey: "stock_quantity",
        enableColumnFilter: false,
        cell: (cell) => {
          const qty = cell.row.original.stock_quantity ?? cell.row.original.stock ?? 0;
          return <span>{typeof qty === "number" ? qty : Number(qty) || 0}</span>;
        },
      },
      {
        header: "Price",
        accessorKey: "price",
        enableColumnFilter: false,
        cell: (cell) => {
          return <Price {...cell} />;
        },
      },
      {
        header: "Orders",
        accessorKey: "orders",
        enableColumnFilter: false,
      },
      {
        header: "Rating",
        accessorKey: "rating",
        enableColumnFilter: false,
        cell: (cell) => {
          return <Rating {...cell} />;
        },
      },
      {
        header: "Published",
        accessorKey: "created_at",
        enableColumnFilter: false,
        cell: (cell) => {
          return <Published {...cell} />;
        },
      },
      {
        header: "Action",
        cell: (cell) => {
          return (
            <UncontrolledDropdown>
              <DropdownToggle 
                href="#" 
                className="btn btn-soft-secondary btn-sm" 
                tag="button"
              >
                <i className="ri-more-fill" />
              </DropdownToggle>
              <DropdownMenu className="dropdown-menu-end">
                <DropdownItem 
                  tag={Link}
                  to={`/apps-ecommerce-product-details/${cell.row.original.id || cell.row.original._id}`}
                >
                  <i className="ri-eye-fill align-bottom me-2 text-muted"></i> View
                </DropdownItem>

                <DropdownItem
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    handleEditClick(cell.row.original);
                  }}
                >
                  <i className="ri-pencil-fill align-bottom me-2 text-muted"></i> Edit
                </DropdownItem>

                <DropdownItem divider />
                <DropdownItem
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    const productData = cell.row.original;
                    onClickDelete(productData);
                  }}
                >
                  <i className="ri-delete-bin-fill align-bottom me-2 text-muted"></i> Delete
                </DropdownItem>
              </DropdownMenu>
            </UncontrolledDropdown>
          );
        },
      },
    ],
    // selectedIds used in cell -> include in deps to update checkboxes
    [selectedIds, categories, subcategories, isSellerProductsSection]
  );

  document.title = "Products - Topcoinland";

  return (
    <div className="page-content">
      <ToastContainer closeButton={false} limit={1} />

      <DeleteModal show={deleteModal} onDeleteClick={handleDeleteProduct} onCloseClick={() => setDeleteModal(false)} />
      <DeleteModal
        show={deleteModalMulti}
        onDeleteClick={() => {
          deleteMultiple();
        }}
        onCloseClick={() => setDeleteModalMulti(false)}
      />
      <Container fluid>
        <BreadCrumb title="Products" pageTitle="Admin" />

        <Row>
          <Col xl={12} lg={12}>
            <div>
              <Card>
                <div className="card-header border-0">
                  <Row className=" align-items-center">
                    <Col>
                      <Nav className="nav-tabs-custom card-header-tabs border-bottom-0" role="tablist">
                        <NavItem>
                          <NavLink className={classnames({ active: activeTab === "1" }, "fw-semibold")} onClick={() => toggleTab("1", "all")} href="#">
                            All <span className="badge bg-danger-subtle text-danger align-middle rounded-pill ms-1">{products.length}</span>
                          </NavLink>
                        </NavItem>
                        <NavItem>
                          <NavLink className={classnames({ active: activeTab === "2" }, "fw-semibold")} onClick={() => toggleTab("2", "published")} href="#">
                            Published <span className="badge bg-danger-subtle text-danger align-middle rounded-pill ms-1">{(Array.isArray(products) ? products.filter(p => p.status === 'published' || p.type === 'published') : []).length}</span>
                          </NavLink>
                        </NavItem>
                        <NavItem>
                          <NavLink className={classnames({ active: activeTab === "3" }, "fw-semibold")} onClick={() => toggleTab("3", "draft")} href="#">
                            Draft <span className="badge bg-danger-subtle text-danger align-middle rounded-pill ms-1">{(Array.isArray(products) ? products.filter(p => p.status === 'draft' || p.type === 'draft' || (!p.status && !p.type)) : []).length}</span>
                          </NavLink>
                        </NavItem>
                      </Nav>
                    </Col>
                    <div className="col-auto">
                      <div className="d-flex gap-2 align-items-center">
                        <Link 
                          to={userRole === "seller" ? "/seller/add-product" : "/add-product"}
                          className="btn btn-info"
                        >
                          <i className="ri-add-circle-line align-bottom me-1"></i>{" "}
                          Add Product
                        </Link>
                        <div id="selection-element" style={{ display: selectedIds.size ? 'block' : 'none' }}>
                          <div className="my-n1 d-flex align-items-center text-muted">
                            Select <div id="select-content" className="text-body fw-semibold px-1">{selectedIds.size}</div> Result
                            <button type="button" className="btn btn-link link-danger p-0 ms-3" onClick={() => setDeleteModalMulti(true)}>
                              Remove
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </Row>
                </div>

                <div className="card-body pt-0" style={{ overflow: 'visible' }}>
                  <style>{`
                    /* Ensure all parent containers allow overflow */
                    .card {
                      overflow: visible !important;
                    }
                    .card-body {
                      overflow: visible !important;
                    }
                    .table-responsive {
                      overflow-x: auto !important;
                      overflow-y: visible !important;
                      position: relative;
                    }
                    .table-responsive table {
                      position: relative;
                      overflow: visible !important;
                    }
                    .table-responsive table tbody {
                      overflow: visible !important;
                    }
                    .table-responsive table tbody tr {
                      position: relative;
                      overflow: visible !important;
                    }
                    .table-responsive table tbody td {
                      overflow: visible !important;
                      position: relative;
                      white-space: nowrap;
                    }
                    .table-responsive table tbody td:last-child {
                      overflow: visible !important;
                    }
                    /* Dropdown positioning */
                    .table-responsive table tbody td .dropdown {
                      position: static !important;
                    }
                    .table-responsive table tbody td .dropdown .dropdown-menu {
                      position: absolute !important;
                      z-index: 1055 !important;
                      right: 0 !important;
                      left: auto !important;
                      top: 100% !important;
                      margin-top: 0.125rem !important;
                      transform: none !important;
                      min-width: 10rem;
                    }
                    .table-responsive table tbody td .dropdown.show .dropdown-menu {
                      display: block !important;
                    }
                  `}</style>
                  {tableData && tableData.length > 0 ? (
                    <TableContainer
                      columns={columns}
                      data={tableData}
                      isGlobalFilter={true}
                      isAddUserList={false}
                      customPageSize={10}
                      divClass="table-responsive mb-1"
                      tableClass="mb-0 align-middle table-borderless"
                      theadClass="table-light text-muted"
                      isProductsFilter={true}
                      SearchPlaceholder={'Search Products...'}
                    />
                  ) : (
                    <div className="py-4 text-center">
                      <div>
                        <lord-icon src="https://cdn.lordicon.com/msoeawqm.json" trigger="loop" colors="primary:#405189,secondary:#0ab39c" style={{ width: "72px", height: "72px" }}></lord-icon>
                      </div>

                      <div className="mt-4">
                        <h5>Sorry! No Result Found</h5>
                      </div>
                    </div>
                  )}
                </div>
              </Card>
            </div>
          </Col>
        </Row>
      </Container>

      {/* Add/Edit Product Modal - Full Featured */}
      <Modal isOpen={modal} toggle={toggle} size="xl" style={{ maxWidth: "95vw" }}>
        <ModalHeader toggle={toggle}>
          {isEdit ? "Edit Product" : "Add Product"}
        </ModalHeader>
        <ModalBody style={{ maxHeight: "90vh", overflowY: "auto" }}>
          <Form
            onSubmit={(e) => {
              e.preventDefault();
              validation.handleSubmit();
              return false;
            }}
          >
            <Row>
              <Col lg={8}>
                {/* Product Title */}
                <Card className="mb-3">
                  <CardBody>
                    <div className="mb-3">
                      <Label className="form-label" htmlFor="product-title-input">
                        Product Title <span className="text-danger">*</span>
                      </Label>
                      <Input
                        type="text"
                        className="form-control"
                        id="product-title-input"
                        placeholder="Enter product title"
                        name="name"
                        value={validation.values.name || ""}
                        onBlur={validation.handleBlur}
                        onChange={validation.handleChange}
                        invalid={
                          validation.errors.name && validation.touched.name
                            ? true
                            : false
                        }
                      />
                      {validation.errors.name && validation.touched.name ? (
                        <FormFeedback type="invalid">
                          {validation.errors.name}
                        </FormFeedback>
                      ) : null}
                    </div>
                  </CardBody>
                </Card>

                {/* Product Description */}
                <Card className="mb-3">
                  <CardBody>
                    <div>
                      <Label>Product Description</Label>
                      <CKEditor
                        editor={ClassicEditor}
                        data={validation.values.description || ""}
                        onChange={(event, editor) => {
                          const data = editor.getData();
                          validation.setFieldValue("description", data);
                        }}
                      />
                    </div>
                  </CardBody>
                </Card>

                {/* Product Gallery */}
                <Card className="mb-3">
                  <CardHeader>
                    <h5 className="card-title mb-0">Product Gallery</h5>
                  </CardHeader>
                  <CardBody>
                    <div className="mb-4">
                      <h5 className="fs-14 mb-1">Product Image <span className="text-danger">*</span></h5>
                      <p className="text-muted mb-3">Upload the main product image (JPG, PNG, or GIF - Max 5MB)</p>
                      <div className="d-flex flex-column align-items-center">
                        <div className="position-relative mb-3">
                          <div className="bg-light rounded border d-flex align-items-center justify-content-center" 
                               style={{ width: "200px", height: "200px", overflow: "hidden" }}>
                            {productImage || existingImage ? (
                              <div className="position-relative w-100 h-100">
                                <img
                                  src={productImage ? URL.createObjectURL(productImage) : (resolveProductImageSrc(existingImage) || "")}
                                  id="product-img"
                                  alt="Product"
                                  className="img-fluid"
                                  style={{ width: "100%", height: "100%", objectFit: "contain" }}
                                />
                                <button
                                  type="button"
                                  className="btn btn-danger btn-sm position-absolute top-0 end-0 m-1 rounded-circle shadow"
                                  style={{ width: "30px", height: "30px", padding: 0, zIndex: 10 }}
                                  onClick={() => {
                                    setProductImage(null);
                                    setExistingImage(null);
                                    const input = document.getElementById("product-image-input");
                                    if (input) input.value = "";
                                    toast.success("Main product image removed");
                                  }}
                                >
                                  <i className="ri-close-line"></i>
                                </button>
                              </div>
                            ) : (
                              <div className="text-center p-3">
                                <i className="ri-image-add-line display-4 text-muted"></i>
                                <p className="text-muted mt-2 mb-0" style={{ fontSize: "14px" }}>No image selected</p>
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="mb-2">
                          <label
                            htmlFor="product-image-input"
                            className="btn btn-primary"
                            style={{ cursor: "pointer" }}
                          >
                            <i className="ri-upload-2-line me-1"></i>
                            {productImage || getProductImageField(productData) ? "Change Image" : "Choose Image"}
                          </label>
                          <input
                            className="form-control d-none"
                            id="product-image-input"
                            type="file"
                            accept="image/png, image/gif, image/jpeg, image/jpg"
                            onChange={(e) => {
                              const file = e.target.files[0];
                              if (file) {
                                // Validate file size (5MB)
                                if (file.size > 5 * 1024 * 1024) {
                                  toast.error("Image size must be less than 5MB");
                                  return;
                                }
                                // Validate file type
                                if (!file.type.match(/^image\/(png|jpeg|jpg|gif)$/)) {
                                  toast.error("Please select a valid image file (PNG, JPG, or GIF)");
                                  return;
                                }
                                setProductImage(file);
                                setExistingImage(null);
                                toast.success("Image selected successfully");
                              }
                            }}
                          />
                        </div>
                        {productImage && (
                          <p className="text-muted mb-0 text-center">
                            <small>Selected: {productImage.name} ({(productImage.size / 1024).toFixed(2)} KB)</small>
                          </p>
                        )}
                      </div>
                    </div>
                    <div>
                      <h5 className="fs-14 mb-1">Product Gallery</h5>
                      <p className="text-muted mb-3">Upload multiple images for the product gallery (JPG, PNG, or GIF - Max 5MB each)</p>
                      <Dropzone
                        onDrop={(acceptedFiles, rejectedFiles) => {
                          if (rejectedFiles && rejectedFiles.length > 0) {
                            rejectedFiles.forEach(({ file, errors }) => {
                              errors.forEach((error) => {
                                if (error.code === "file-too-large") {
                                  toast.error(`${file.name} is too large. Maximum size is 5MB.`);
                                } else if (error.code === "file-invalid-type") {
                                  toast.error(`${file.name} is not a valid image file.`);
                                } else {
                                  toast.error(`${file.name}: ${error.message}`);
                                }
                              });
                            });
                          }
                          if (acceptedFiles && acceptedFiles.length > 0) {
                            handleAcceptedFiles(acceptedFiles);
                            toast.success(`${acceptedFiles.length} image(s) added to gallery`);
                          }
                        }}
                        accept={{
                          'image/*': ['.png', '.jpg', '.jpeg', '.gif']
                        }}
                        maxSize={5 * 1024 * 1024} // 5MB
                        multiple={true}
                      >
                        {({ getRootProps, getInputProps, isDragActive }) => (
                          <div 
                            className={`dropzone dz-clickable border rounded p-4 ${isDragActive ? 'border-primary bg-light' : ''}`}
                            style={{ 
                              borderStyle: "dashed",
                              transition: "all 0.3s ease",
                              cursor: "pointer"
                            }}
                          >
                            <div
                              className="dz-message needsclick text-center"
                              {...getRootProps()}
                            >
                              <input {...getInputProps()} />
                              <div className="mb-3">
                                <i className={`display-4 ${isDragActive ? 'text-primary' : 'text-muted'} ri-upload-cloud-2-fill`} />
                              </div>
                              <h5 className={isDragActive ? 'text-primary' : ''}>
                                {isDragActive ? "Drop images here" : "Drop images here or click to upload"}
                              </h5>
                              <p className="text-muted mb-0">
                                <small>You can upload multiple images at once</small>
                              </p>
                            </div>
                          </div>
                        )}
                      </Dropzone>
                      {selectedFiles.length > 0 && (
                        <div className="mt-3">
                          <h6 className="mb-2">Gallery Images ({selectedFiles.length})</h6>
                          <Row className="g-2">
                            {selectedFiles.map((f, i) => {
                              return (
                                <Col key={i + "-file"} xs={6} sm={4} md={3} lg={2}>
                                  <Card className="mb-2 shadow-sm border position-relative">
                                    <div className="position-relative">
                                      <img
                                        src={f.preview}
                                        alt={f.name}
                                        className="img-fluid rounded-top"
                                        style={{ width: "100%", height: "150px", objectFit: "cover" }}
                                      />
                                      <button
                                        type="button"
                                        className="btn btn-danger btn-sm position-absolute top-0 end-0 m-1 rounded-circle"
                                        style={{ width: "28px", height: "28px", padding: 0, fontSize: "14px" }}
                                        onClick={() => {
                                          const newFiles = selectedFiles.filter((_, idx) => idx !== i);
                                          setselectedFiles(newFiles);
                                          toast.success("Image removed from gallery");
                                        }}
                                      >
                                        <i className="ri-close-line"></i>
                                      </button>
                                    </div>
                                    <CardBody className="p-2">
                                      <p className="text-truncate mb-0" style={{ fontSize: "12px" }} title={f.name}>
                                        {f.name}
                                      </p>
                                      <small className="text-muted">{f.formattedSize}</small>
                                    </CardBody>
                                  </Card>
                                </Col>
                              );
                            })}
                          </Row>
                        </div>
                      )}
                    </div>
                  </CardBody>
                </Card>

                {/* General Info / Meta Data Tabs */}
                <Card className="mb-3">
                  <CardHeader>
                    <Nav className="nav-tabs-custom card-header-tabs border-bottom-0">
                      <NavItem>
                        <NavLink
                          style={{ cursor: "pointer" }}
                          className={classnames({
                            active: customActiveTab === "1",
                          })}
                          onClick={() => {
                            toggleCustom("1");
                          }}
                        >
                          General Info
                        </NavLink>
                      </NavItem>
                      <NavItem>
                        <NavLink
                          style={{ cursor: "pointer" }}
                          className={classnames({
                            active: customActiveTab === "2",
                          })}
                          onClick={() => {
                            toggleCustom("2");
                          }}
                        >
                          Meta Data
                        </NavLink>
</NavItem>
                    </Nav>
                  </CardHeader>

                  <CardBody>
                    <TabContent activeTab={customActiveTab}>
                      <TabPane id="addproduct-general-info" tabId="1">
                        {/* Product Options & Variants Component */}
                        <ProductVariantManager
                          hasVariants={hasVariants}
                          onHasVariantsChange={(val) => {
                            setHasVariants(val);
                            if (!val) {
                              setVariants([]);
                            }
                          }}
                          options={options}
                          onOptionsChange={setOptions}
                          variants={variants}
                          onVariantsChange={setVariants}
                          basePrice={validation.values.price}
                          baseSku={validation.values.stock}
                          baseStock={validation.values.stock_quantity}
                          currencySymbol="£"
                        />

                        {hasVariants ? (
                          <div className="alert alert-info py-2 px-3 mb-3 d-flex align-items-center gap-2">
                            <i className="ri-information-line fs-18"></i>
                            <div>
                              <strong>Variable Product Pricing Active:</strong> Individual variant prices, SKUs, and stock quantities are configured in the table above.
                            </div>
                          </div>
                        ) : (
                          <Row>
                            <Col sm={3}>
                              <div className="mb-3">
                                <label
                                  className="form-label"
                                  htmlFor="product-stock-input"
                                >
                                  SKU <span className="text-danger">*</span>
                                </label>
                                <Input
                                  type="text"
                                  className="form-control"
                                  id="product-stock-input"
                                  placeholder="Enter SKU"
                                  name="stock"
                                  value={validation.values.stock || ""}
                                  onBlur={validation.handleBlur}
                                  onChange={validation.handleChange}
                                  invalid={
                                    validation.errors.stock &&
                                    validation.touched.stock
                                      ? true
                                      : false
                                  }
                                />
                                {validation.errors.stock &&
                                validation.touched.stock ? (
                                  <FormFeedback type="invalid">
                                    {validation.errors.stock}
                                  </FormFeedback>
                                ) : null}
                              </div>
                            </Col>

                            <Col sm={3}>
                              <div className="mb-3">
                                <label
                                  className="form-label"
                                  htmlFor="product-stock-qty-input"
                                >
                                  Stock QTY <span className="text-danger">*</span>
                                </label>
                                <Input
                                  type="number"
                                  min="0"
                                  className="form-control"
                                  id="product-stock-qty-input"
                                  placeholder="0"
                                  name="stock_quantity"
                                  value={validation.values.stock_quantity ?? ""}
                                  onBlur={validation.handleBlur}
                                  onChange={validation.handleChange}
                                  invalid={
                                    validation.errors.stock_quantity &&
                                    validation.touched.stock_quantity
                                      ? true
                                      : false
                                  }
                                />
                                {validation.errors.stock_quantity &&
                                validation.touched.stock_quantity ? (
                                  <FormFeedback type="invalid">
                                    {validation.errors.stock_quantity}
                                  </FormFeedback>
                                ) : null}
                              </div>
                            </Col>

                            <Col sm={3}>
                              <div className="mb-3">
                                <label
                                  className="form-label"
                                  htmlFor="product-price-input"
                                >
                                  Price <span className="text-danger">*</span>
                                </label>
                                <div className="input-group mb-3">
                                  <span
                                    className="input-group-text"
                                    id="product-price-addon"
                                  >
                                    £
                                  </span>
                                  <Input
                                    type="text"
                                    className="form-control"
                                    id="product-price-input"
                                    placeholder="Enter price"
                                    name="price"
                                    aria-label="Price"
                                    aria-describedby="product-price-addon"
                                    value={validation.values.price || ""}
                                    onBlur={validation.handleBlur}
                                    onChange={validation.handleChange}
                                    invalid={
                                      validation.errors.price &&
                                      validation.touched.price
                                        ? true
                                        : false
                                    }
                                  />
                                  {validation.errors.price &&
                                  validation.touched.price ? (
                                    <FormFeedback type="invalid">
                                      {validation.errors.price}
                                    </FormFeedback>
                                  ) : null}
                                </div>
                              </div>
                            </Col>

                            <Col sm={3}>
                              <div className="mb-3">
                                <label
                                  className="form-label"
                                  htmlFor="product-purchase-price-input"
                                >
                                  Purchase Price
                                </label>
                                <div className="input-group mb-3">
                                  <span
                                    className="input-group-text"
                                    id="product-purchase-price-addon"
                                  >
                                    £
                                  </span>
                                  <Input
                                    type="text"
                                    className="form-control"
                                    id="product-purchase-price-input"
                                    placeholder="Enter purchase price"
                                    name="purchase_price"
                                    aria-label="Purchase Price"
                                    aria-describedby="product-purchase-price-addon"
                                    value={validation.values.purchase_price || ""}
                                    onBlur={validation.handleBlur}
                                    onChange={validation.handleChange}
                                    invalid={
                                      validation.errors.purchase_price &&
                                      validation.touched.purchase_price
                                        ? true
                                        : false
                                    }
                                  />
                                  {validation.errors.purchase_price &&
                                  validation.touched.purchase_price ? (
                                    <FormFeedback type="invalid">
                                      {validation.errors.purchase_price}
                                    </FormFeedback>
                                  ) : null}
                                </div>
                              </div>
                            </Col>
                          </Row>
                        )}

                        <Row>
                          <Col sm={3}>
                            <div className="mb-3">
                              <label
                                className="form-label"
                                htmlFor="product-Discount-input"
                              >
                                Discount
                              </label>
                              <div className="input-group mb-3">
                                <span
                                  className="input-group-text"
                                  id="product-Discount-addon"
                                >
                                  %
                                </span>
                                <Input
                                  type="text"
                                  className="form-control"
                                  id="product-Discount-input"
                                  placeholder="Enter Discount"
                                  name="product_discount"
                                  aria-label="product_discount"
                                  aria-describedby="product-orders-addon"
                                  value={validation.values.product_discount || ""}
                                  onBlur={validation.handleBlur}
                                  onChange={validation.handleChange}
                                />
                              </div>
                            </div>
                          </Col>

                          <Col sm={3}>
                            <div className="mb-3">
                              <label
                                className="form-label"
                                htmlFor="product-commission-input"
                              >
                                Commission
                              </label>
                              <Input
                                name="commission"
                                type="select"
                                className="form-select"
                                id="commission-field"
                                onChange={validation.handleChange}
                                onBlur={validation.handleBlur}
                                value={validation.values.commission || ""}
                              >
                                <option value="">Select Commission</option>
                                {commissionType.map((item, key) => (
                                  <option value={item.value} key={key}>
                                    {item.label}
                                  </option>
                                ))}
                              </Input>
                            </div>
                          </Col>

                          <Col sm={3}>
                            <div className="mb-3">
                              <label
                                className="form-label"
                                htmlFor="product-tax-input"
                              >
                                Tax
                              </label>
                              <Input
                                name="tax"
                                type="select"
                                className="form-select"
                                id="tax-field"
                                onChange={validation.handleChange}
                                onBlur={validation.handleBlur}
                                value={validation.values.tax || ""}
                              >
                                <option value="">Select Tax</option>
                                {taxType.map((item, key) => (
                                  <option value={item.value} key={key}>
                                    {item.label}
                                  </option>
                                ))}
                              </Input>
                            </div>
                          </Col>
                        </Row>

                        {/* Product Attributes Section */}
                        <div className="mt-4">
                          <h5 className="fs-16 mb-3">Product Attributes</h5>
                          <Row>
                            <Col sm={6}>
                              <div className="mb-3">
                                <label className="form-label" htmlFor="product-material-input">
                                  Material
                                </label>
                                <Input
                                  type="text"
                                  className="form-control"
                                  id="product-material-input"
                                  placeholder="e.g., Cotton, Polyester, Leather"
                                  name="material"
                                  value={validation.values.material || ""}
                                  onChange={validation.handleChange}
                                  onBlur={validation.handleBlur}
                                />
                              </div>
                            </Col>
                            <Col sm={6}>
                              <div className="mb-3">
                                <label className="form-label" htmlFor="product-weight-input">
                                  Weight (kg)
                                </label>
                                <Input
                                  type="number"
                                  step="0.01"
                                  className="form-control"
                                  id="product-weight-input"
                                  placeholder="0.00"
                                  name="weight"
                                  value={validation.values.weight || ""}
                                  onChange={validation.handleChange}
                                  onBlur={validation.handleBlur}
                                  invalid={validation.errors.weight && validation.touched.weight ? true : false}
                                />
                                {validation.errors.weight && validation.touched.weight ? (
                                  <FormFeedback type="invalid">
                                    {validation.errors.weight}
                                  </FormFeedback>
                                ) : null}
                              </div>
                            </Col>
                          </Row>

                          <Row>
                            <Col sm={4}>
                              <div className="mb-3">
                                <label className="form-label" htmlFor="product-dimension-length-input">
                                  Length (cm)
                                </label>
                                <Input
                                  type="number"
                                  step="0.01"
                                  className="form-control"
                                  id="product-dimension-length-input"
                                  placeholder="0.00"
                                  name="dimensions_length"
                                  value={validation.values.dimensions_length || ""}
                                  onChange={validation.handleChange}
                                  onBlur={validation.handleBlur}
                                />
                              </div>
                            </Col>
                            <Col sm={4}>
                              <div className="mb-3">
                                <label className="form-label" htmlFor="product-dimension-width-input">
                                  Width (cm)
                                </label>
                                <Input
                                  type="number"
                                  step="0.01"
                                  className="form-control"
                                  id="product-dimension-width-input"
                                  placeholder="0.00"
                                  name="dimensions_width"
                                  value={validation.values.dimensions_width || ""}
                                  onChange={validation.handleChange}
                                  onBlur={validation.handleBlur}
                                />
                              </div>
                            </Col>
                            <Col sm={4}>
                              <div className="mb-3">
                                <label className="form-label" htmlFor="product-dimension-height-input">
                                  Height (cm)
                                </label>
                                <Input
                                  type="number"
                                  step="0.01"
                                  className="form-control"
                                  id="product-dimension-height-input"
                                  placeholder="0.00"
                                  name="dimensions_height"
                                  value={validation.values.dimensions_height || ""}
                                  onChange={validation.handleChange}
                                  onBlur={validation.handleBlur}
                                />
                              </div>
                            </Col>
                          </Row>

                          <Row>
                            <Col sm={12}>
                              <div className="mb-3">
                                <label className="form-label" htmlFor="product-features-input">
                                  Features (one per line or comma-separated)
                                </label>
                                <textarea
                                  className="form-control"
                                  id="product-features-input"
                                  placeholder="e.g., Machine Washable, Waterproof, 100% Organic"
                                  name="features"
                                  rows="4"
                                  value={validation.values.features || ""}
                                  onChange={validation.handleChange}
                                  onBlur={validation.handleBlur}
                                />
                                <small className="text-muted">Enter features, each on a new line or separated by commas</small>
                              </div>
                            </Col>
                          </Row>
                        </div>
                      </TabPane>

                      <TabPane id="addproduct-metadata" tabId="2">
                        <Row>
                          <Col lg={6}>
                            <div className="mb-3">
                              <Label
                                className="form-label"
                                htmlFor="meta-title-input"
                              >
                                Meta title
                              </Label>
                              <Input
                                type="text"
                                className="form-control"
                                placeholder="Enter meta title"
                                id="meta-title-input"
                                name="meta_title"
                                value={validation.values.meta_title || ""}
                                onBlur={validation.handleBlur}
                                onChange={validation.handleChange}
                              />
                            </div>
                          </Col>

                          <Col lg={6}>
                            <div className="mb-3">
                              <Label
                                className="form-label"
                                htmlFor="meta-keywords-input"
                              >
                                Meta Keywords
                              </Label>
                              <Input
                                type="text"
                                className="form-control"
                                placeholder="Enter meta keywords"
                                id="meta-keywords-input"
                                name="meta_keyword"
                                value={validation.values.meta_keyword || ""}
                                onBlur={validation.handleBlur}
                                onChange={validation.handleChange}
                              />
                            </div>
                          </Col>
                        </Row>

                        <div>
                          <Label
                            className="form-label"
                            htmlFor="meta-description-input"
                          >
                            Meta Description
                          </Label>
                          <textarea
                            className="form-control"
                            id="meta-description-input"
                            placeholder="Enter meta description"
                            name="meta_description"
                            rows="3"
                            value={validation.values.meta_description || ""}
                            onChange={validation.handleChange}
                          ></textarea>
                        </div>
                      </TabPane>
                    </TabContent>
                  </CardBody>
                </Card>

                <div className="text-end mb-3">
                  <Button type="button" color="light" onClick={toggle} className="me-2">
                    Cancel
                  </Button>
                  <Button type="submit" color="success">
                    {isEdit ? "Update" : "Submit"}
                  </Button>
                </div>
              </Col>

              <Col lg={4}>
                {/* Publish */}
                <Card className="mb-3">
                  <CardHeader>
                    <h5 className="card-title mb-0">Publish</h5>
                  </CardHeader>
                  <CardBody>
                    <div className="mb-3">
                      <Label
                        htmlFor="choices-publish-status-input"
                        className="form-label"
                      >
                        Status <span className="text-danger">*</span>
                      </Label>
                      <Input
                        name="status"
                        type="select"
                        className="form-select"
                        id="choices-publish-status-input"
                        onChange={validation.handleChange}
                        onBlur={validation.handleBlur}
                        value={validation.values.status || ""}
                        invalid={
                          validation.touched.status && validation.errors.status
                            ? true
                            : false
                        }
                      >
                        <option value="draft">Draft</option>
                        <option value="published">Published</option>
                        <option value="scheduled">Scheduled</option>
                      </Input>
                      {validation.touched.status &&
                        validation.errors.status ? (
                        <FormFeedback type="invalid">
                          {validation.errors.status}
                        </FormFeedback>
                      ) : null}
                    </div>

                    <div>
                      <Label
                        htmlFor="choices-publish-visibility-input"
                        className="form-label"
                      >
                        Visibility
                      </Label>
                      <Select
                        value={selectedVisibility}
                        onChange={(selected) => {
                          setselectedVisibility(selected);
                          validation.setFieldValue("visibility", selected?.value || "");
                        }}
                        options={productVisibility}
                        name="choices-publish-visibility-input"
                        classNamePrefix="select2-selection form-select"
                      />
                    </div>
                  </CardBody>
                </Card>

                {/* Store - Only show for Seller Products */}
                {showStoreDropdown && (
                  <Card className="mb-3">
                    <CardHeader>
                      <h5 className="card-title mb-0">Store</h5>
                    </CardHeader>
                    <CardBody>
                      <Input
                        name="category_id"
                        type="select"
                        className="form-select"
                        id="category-field"
                        onChange={(e) => {
                          const selectedCategoryId = e.target.value;
                          validation.handleChange(e);
                          validation.setFieldValue("subcategory_id", ""); // Reset subcategory when category changes
                          
                          // Debug: Log to see what's happening
                          console.log("Store Category selected:", selectedCategoryId);
                          console.log("Available subcategories:", subcategories);
                          console.log("Filtered subcategories:", subcategories.filter((subcat) => {
                            const subcatCategoryId = String(subcat.category_id || subcat.categoryId || "");
                            const selectedId = String(selectedCategoryId);
                            return subcatCategoryId === selectedId;
                          }));
                        }}
                        onBlur={validation.handleBlur}
                        value={validation.values.category_id || ""}
                        invalid={
                          validation.touched.category_id && validation.errors.category_id
                            ? true
                            : false
                        }
                      >
                        <option value="">Select Category</option>
                        {categories.map((category) => (
                          <option key={category.id || category._id} value={category.id || category._id}>
                            {category.name}
                          </option>
                        ))}
                      </Input>
                      {validation.touched.category_id && validation.errors.category_id ? (
                        <FormFeedback type="invalid">
                          {validation.errors.category_id}
                        </FormFeedback>
                      ) : null}
                    </CardBody>
                  </Card>
                )}

                {/* Product Categories */}
                <Card className="mb-3">
                  <CardHeader>
                    <h5 className="card-title mb-0">Product Categories</h5>
                  </CardHeader>
                  <CardBody>
                    <p className="text-muted mb-2">
                      Select Category
                    </p>
                    <Input
                      name="category_id"
                      type="select"
                      className="form-select"
                      id="category-field-2"
                      onChange={(e) => {
                        validation.handleChange(e);
                        validation.setFieldValue("subcategory_id", "");
                      }}
                      onBlur={validation.handleBlur}
                      value={validation.values.category_id || ""}
                      invalid={
                        validation.touched.category_id && validation.errors.category_id
                          ? true
                          : false
                      }
                    >
                      <option value="">Select Category</option>
                      {categories.map((category) => (
                        <option key={category.id || category._id} value={category.id || category._id}>
                          {category.name}
                        </option>
                      ))}
                    </Input>
                    {validation.touched.category_id && validation.errors.category_id ? (
                      <FormFeedback type="invalid">
                        {validation.errors.category_id}
                      </FormFeedback>
                    ) : null}
                    <div className="mt-3">
                      <p className="text-muted mb-2">
                        Select Sub Category
                      </p>
                      <Input
                        name="subcategory_id"
                        type="select"
                        className="form-select"
                        id="subcategory-field"
                        onChange={validation.handleChange}
                        onBlur={validation.handleBlur}
                        value={validation.values.subcategory_id || ""}
                        disabled={!validation.values.category_id}
                        invalid={
                          validation.touched.subcategory_id && validation.errors.subcategory_id
                            ? true
                            : false
                        }
                      >
                        <option value="">Select Subcategory</option>
                        {filteredSubcategories.length > 0 ? (
                          filteredSubcategories.map((subcategory) => (
                            <option key={subcategory.id || subcategory._id} value={subcategory.id || subcategory._id}>
                              {subcategory.name}
                            </option>
                          ))
                        ) : validation.values.category_id ? (
                          <option value="" disabled>No subcategories found for this category</option>
                        ) : null}
                      </Input>
                      {validation.touched.subcategory_id && validation.errors.subcategory_id ? (
                        <FormFeedback type="invalid">
                          {validation.errors.subcategory_id}
                        </FormFeedback>
                      ) : null}
                    </div>
                  </CardBody>
                </Card>

                {/* Product Tags */}
                <Card className="mb-3">
                  <CardHeader>
                    <h5 className="card-title mb-0">Product Tags</h5>
                  </CardHeader>
                  <CardBody>
                    <div className="hstack gap-3 align-items-start">
                      <div className="flex-grow-1">
                        <Input
                          className="form-control"
                          placeholder="Enter tags"
                          type="text"
                          name="product_tags"
                          value={validation.values.product_tags || ""}
                          onBlur={validation.handleBlur}
                          onChange={validation.handleChange}
                        />
                      </div>
                    </div>
                  </CardBody>
                </Card>

                {/* Product Short Description */}
                <Card className="mb-3">
                  <CardHeader>
                    <h5 className="card-title mb-0">Product Short Description</h5>
                  </CardHeader>
                  <CardBody>
                    <p className="text-muted mb-2">
                      Add short description for product
                    </p>
                    <textarea
                      className="form-control"
                      placeholder="Must enter minimum of a 100 characters"
                      rows="3"
                      name="short_description"
                      value={validation.values.short_description || ""}
                      onChange={validation.handleChange}
                    ></textarea>
                  </CardBody>
                </Card>
              </Col>
            </Row>
          </Form>
        </ModalBody>
      </Modal>
    </div>
  );
};

export default EcommerceProducts;
